import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tab,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch
} from '@mui/material';
import ConsoleTabs from '../common/ConsoleTabs';
import {
  PlayArrow,
  Update,
  CheckCircle,
  Error,
  Warning,
  Info,
  Refresh,
  CalendarToday,
  Stop,
  Save as SaveIcon,
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { newImportService } from '../../services/newImportService';
import { backupRestoreService } from '../../services/backupRestoreService';
import { authGet, authPut, authPost } from '../../services/apiService';
import { format } from 'date-fns';
import ProcessingConfig from './ProcessingConfig';

const FULL_IMPORT_DARK_GREEN = '#1D4434';

/** Default WIQL for Full Import (used when config is empty or when user clicks Load defaults) */
const DEFAULT_FULL_IMPORT_WIQL = {
  workItems: "SELECT [System.Id] FROM WorkItems WHERE [System.TeamProject] = 'HighQ Tech' AND [System.WorkItemType] IN ('Epic','Feature','Product Backlog Item','Bug','Task') AND [System.State] <> 'Removed' ORDER BY [System.Id]",
  testCase: "SELECT [System.Id] FROM WorkItems WHERE [System.TeamProject] = 'HighQ Tech' AND [System.WorkItemType] = 'Test Case' AND [System.State] NOT IN ('Closed', 'Done', 'Removed') ORDER BY [System.Id]",
  rca: "SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType] = 'RCA'",
  technicalDebt: "SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType] = 'Task' AND [System.Tags] CONTAINS 'TechnicalDebt'"
};

/** Legacy Test Case default (only Removed excluded); when saved config equals this, show new default */
const LEGACY_TEST_CASE_WIQL = "SELECT [System.Id] FROM WorkItems WHERE [System.TeamProject] = 'HighQ Tech' AND [System.WorkItemType] = 'Test Case' AND [System.State] <> 'Removed' ORDER BY [System.Id]";

/** Field metadata for each Full Import work item type (fallback when server display config not loaded) */
const FULL_IMPORT_FIELD_META = {
  workItems: {
    commonFields: [
      'System.Id', 'System.Title', 'System.Description', 'System.WorkItemType', 'System.State',
      'System.AssignedTo', 'System.AreaPath', 'System.IterationPath', 'System.Tags',
      'System.CreatedDate', 'System.ChangedDate', 'System.Parent', 'System.TeamProject',
      'Microsoft.VSTS.Common.Priority', 'Microsoft.VSTS.Common.Severity', 'Microsoft.VSTS.Common.ClosedDate',
      'Microsoft.VSTS.Common.ResolvedDate', 'Microsoft.VSTS.Scheduling.StoryPoints', 'Microsoft.VSTS.Scheduling.TargetDate',
      'Microsoft.VSTS.Scheduling.OriginalEstimate', 'Microsoft.VSTS.Scheduling.RemainingWork', 'Microsoft.VSTS.Scheduling.CompletedWork',
      'Custom.Team', 'Custom.Fix_Version_Dummy', 'Custom.Affects_Version_Dummy', 'Custom.IssueSource', 'Custom.Environment', 'Custom.RCA'
    ],
    typeFields: {
      Epic: ['Microsoft.VSTS.Scheduling.TargetDate'],
      Feature: ['Microsoft.VSTS.Scheduling.TargetDate'],
      'Product Backlog Item': ['Microsoft.VSTS.Scheduling.StoryPoints'],
      Bug: ['Microsoft.VSTS.Scheduling.StoryPoints', 'Custom.IssueSource', 'Custom.Affects_Version_Dummy', 'Custom.component_dummy', 'Custom.subcomponent_dummy'],
      Task: ['Microsoft.VSTS.Scheduling.OriginalEstimate', 'Microsoft.VSTS.Scheduling.RemainingWork', 'Microsoft.VSTS.Scheduling.CompletedWork'],
      Test: ['Custom.AutomarionStatus', 'Microsoft.VSTS.TCM.TestSuiteType']
    },
    calculated: [
      'parent (from System.Parent relation)',
      'ageDays (closedDate − createdDate)',
      'completionPercentage',
      'testedBy (from relations)',
      'fixVersion, component, subComponent (from custom fields)'
    ]
  },
  testCase: {
    fetched: [
      'System.Id', 'System.Title', 'System.State', 'System.WorkItemType', 'System.CreatedDate', 'System.ChangedDate',
      'System.AssignedTo', 'System.IterationPath', 'System.AreaPath', 'System.Tags', 'System.Parent',
      'System.Description', 'Microsoft.VSTS.Common.ClosedDate', 'Microsoft.VSTS.Common.Priority',
      'Custom.Team', 'Custom.Fix_Version_Dummy', 'Custom.Environment',
      'Custom.AutomarionStatus', 'Microsoft.VSTS.TCM.TestSuiteType'
    ],
    calculated: [
      'priority (clamped 1–4)',
      'title (fallback if empty)',
      'testSuite (default Unknown)',
      'sprint, release, assignee, state, areaPath (mapped from ADO)'
    ]
  },
  rca: {
    fetched: [
      'System.Id', 'System.Title', 'System.Description', 'System.WorkItemType', 'System.State',
      'System.AssignedTo', 'System.CreatedBy', 'System.AreaPath', 'System.IterationPath', 'System.Tags',
      'System.CreatedDate', 'System.ChangedDate', 'System.TeamProject', 'Microsoft.VSTS.Common.ClosedDate',
      'Microsoft.VSTS.Common.Priority', 'Microsoft.VSTS.Common.Severity', 'Microsoft.VSTS.Scheduling.StoryPoints',
      'Microsoft.VSTS.Scheduling.OriginalEstimate', 'Microsoft.VSTS.Scheduling.Effort',
      'Microsoft.VSTS.Scheduling.TargetDate', 'Microsoft.VSTS.Scheduling.DueDate', 'Custom.Team', 'Team'
    ],
    calculated: [
      'assignedTo, createdBy (displayName from identity)',
      'dueDate (from TargetDate/DueDate/Custom.DueDate)',
      'team (from Custom.Team or derived from AreaPath)',
      'tags (split by semicolon)', 'syncedAt', 'Related RCA Actions (from Relations)'
    ]
  },
  technicalDebt: {
    fetched: [
      'System.Id', 'System.Title', 'System.Description', 'System.WorkItemType', 'System.State',
      'System.AssignedTo', 'System.CreatedBy', 'System.AreaPath', 'System.IterationPath', 'System.Tags',
      'System.CreatedDate', 'System.ChangedDate', 'System.TeamProject', 'Microsoft.VSTS.Common.ClosedDate',
      'Microsoft.VSTS.Common.Priority', 'Microsoft.VSTS.Common.Severity', 'Microsoft.VSTS.Scheduling.StoryPoints',
      'Microsoft.VSTS.Scheduling.OriginalEstimate', 'Microsoft.VSTS.Scheduling.Effort',
      'Microsoft.VSTS.Scheduling.TargetDate', 'Microsoft.VSTS.Scheduling.DueDate', 'Custom.Team', 'Team'
    ],
    calculated: [
      'assignedTo, createdBy (displayName from identity)',
      'dueDate (from TargetDate/DueDate/Custom.DueDate)',
      'team (from Custom.Team or derived from AreaPath)',
      'tags (split by semicolon)', 'effort (StoryPoints or OriginalEstimate)', 'syncedAt', 'customFields (remaining)'
    ]
  }
};

/** Chunk array into groups of size n */
function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

/** Work Items right panel: Common fields table + Fields by work item type table (multiple fields per row, no scroll) */
function WorkItemsFieldsTables({ config, accentColor }) {
  const commonFields = config?.commonFields || [];
  const typeFields = config?.typeFields || {};
  const calculated = config?.calculated || [];
  const commonRows = chunk(commonFields, 5);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: accentColor }}>Common fields</Typography>
      <TableContainer sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell sx={{ fontWeight: 600, py: 0.5 }}>Field</TableCell></TableRow></TableHead>
          <TableBody>
            {commonRows.map((rowFields, i) => (
              <TableRow key={i}><TableCell sx={{ py: 0.25, fontSize: '0.75rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>{rowFields.join(', ')}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" sx={{ fontWeight: 600, color: accentColor, display: 'block', mt: 1 }}>Fields by work item type</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow><TableCell sx={{ fontWeight: 600, py: 0.5 }}>Type</TableCell><TableCell sx={{ fontWeight: 600, py: 0.5 }}>Fields</TableCell></TableRow></TableHead>
          <TableBody>
            {Object.entries(typeFields).map(([type, fields]) => (
              <TableRow key={type}><TableCell sx={{ py: 0.25, fontSize: '0.75rem' }}>{type}</TableCell><TableCell sx={{ py: 0.25, fontSize: '0.75rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>{(fields || []).join(', ')}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {calculated.length > 0 && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 600, color: accentColor, display: 'block', mt: 1 }}>Calculated</Typography>
          <Typography component="div" variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>{calculated.join('; ')}</Typography>
        </>
      )}
    </Paper>
  );
}

/** Table: Fetched and Calculated with multiple fields per row (no scroll) */
function FetchedCalculatedTable({ fetched = [], calculated = [], accentColor }) {
  const fetchedRows = chunk(fetched, 5);
  const calculatedRows = chunk(calculated, 3);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow><TableCell sx={{ fontWeight: 600, py: 0.5, color: accentColor }}>Kind</TableCell><TableCell sx={{ fontWeight: 600, py: 0.5, color: accentColor }}>Field / Description</TableCell></TableRow></TableHead>
          <TableBody>
            {fetchedRows.map((fields, i) => (
              <TableRow key={`f-${i}`}><TableCell sx={{ py: 0.25, fontSize: '0.75rem' }}>Fetched</TableCell><TableCell sx={{ py: 0.25, fontSize: '0.75rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>{fields.join(', ')}</TableCell></TableRow>
            ))}
            {calculatedRows.map((items, i) => (
              <TableRow key={`c-${i}`}><TableCell sx={{ py: 0.25, fontSize: '0.75rem' }}>Calculated</TableCell><TableCell sx={{ py: 0.25, fontSize: '0.75rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>{items.join('; ')}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/**
 * Unified Import Settings component combining ADO items and Test items imports
 * @param {Function} onImportStart - Callback when import starts
 * @param {Function} onImportComplete - Callback when import completes
 * @returns {JSX.Element} UnifiedImportSettings component
 */
const UnifiedImportSettings = ({ onImportStart, onImportComplete }) => {
  const [isFullImportRunning, setIsFullImportRunning] = useState(false);
  const [isDeltaImportRunning, setIsDeltaImportRunning] = useState(false);
  const [isImportAfterDateRunning, setIsImportAfterDateRunning] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [importStats, setImportStats] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [adoConfig, setAdoConfig] = useState({ queryUrl: '' });
  const [isDiscoveringFields, setIsDiscoveringFields] = useState(false);
  const [discoveredFields, setDiscoveredFields] = useState(null);
  const [isMonthlyImportRunning, setIsMonthlyImportRunning] = useState(false);
  const [isBuildingHierarchies, setIsBuildingHierarchies] = useState(false);
  const [isRebuildingHierarchies, setIsRebuildingHierarchies] = useState(false);

  // Work item type selection (used by Delta Imports and Update after date)
  const [selectedWorkItemTypes, setSelectedWorkItemTypes] = useState({
    Epic: true,
    Feature: true,
    'Product Backlog Item': true,
    Bug: true,
    Task: true,
    'Test Case': true
  });
  
  // Import history
  const [importHistory, setImportHistory] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [pollingLogId, setPollingLogId] = useState(null);
  const [hasRunningImport, setHasRunningImport] = useState(false);

  const workItemTypes = [
    { key: 'Epic', label: 'Epic' },
    { key: 'Feature', label: 'Feature' },
    { key: 'Product Backlog Item', label: 'Product Backlog Item' },
    { key: 'Bug', label: 'Bug' },
    { key: 'Task', label: 'Task' },
    { key: 'Test Case', label: 'Test Case' }
  ];
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Processing config state
  const [processingConfig, setProcessingConfig] = useState(null);

  // Delta import config state
  const [deltaImportConfig, setDeltaImportConfig] = useState({
    cronSchedule: '0 */2 * * *',
    hoursBack: 3,
    lastRun: null,
    nextRun: null
  });
  const [isSavingDeltaConfig, setIsSavingDeltaConfig] = useState(false);
  
  // Test import state
  const [testConfig, setTestConfig] = useState({ queryUrl: '', discoveredFields: [] });
  const [testDeltaImportConfig, setTestDeltaImportConfig] = useState({
    cronSchedule: '0 */2 * * *',
    hoursBack: 3,
    lastRun: null,
    nextRun: null
  });
  const [runDeltaHoursBack, setRunDeltaHoursBack] = useState(4);
  const [isTestMonthlyImportRunning, setIsTestMonthlyImportRunning] = useState(false);
  const [isTestDeltaImportRunning, setIsTestDeltaImportRunning] = useState(false);
  const [isTestImportAfterDateRunning, setIsTestImportAfterDateRunning] = useState(false);
  const [isDiscoveringTestFields, setIsDiscoveringTestFields] = useState(false);
  const [isSavingTestConfig, setIsSavingTestConfig] = useState(false);
  const [testSelectedDate, setTestSelectedDate] = useState('');

  // Backup & Restore
  const [backups, setBackups] = useState([]);
  const [selectedBackup, setSelectedBackup] = useState('');
  const [backupName, setBackupName] = useState('');
  const [isBackupRunning, setIsBackupRunning] = useState(false);
  const [isRestoreRunning, setIsRestoreRunning] = useState(false);

  // Full Import tab (4 WIQL areas: Work Items, Test Case, RCA, Technical Debt)
  const [loadingFullImportConfig, setLoadingFullImportConfig] = useState(false);
  const [savingFullImportConfig, setSavingFullImportConfig] = useState(false);
  const [workItemsWiqlQuery, setWorkItemsWiqlQuery] = useState('');
  const [testCaseWiqlQuery, setTestCaseWiqlQuery] = useState('');
  const [rcaWiqlQuery, setRcaWiqlQuery] = useState('');
  const [technicalDebtWiqlQuery, setTechnicalDebtWiqlQuery] = useState('');
  const [lastRcaSync, setLastRcaSync] = useState(null);
  const [lastTechnicalDebtSync, setLastTechnicalDebtSync] = useState(null);
  const [lastFullImport, setLastFullImport] = useState(null);
  const [syncingRCA, setSyncingRCA] = useState(false);
  const [fullImportDisplayConfig, setFullImportDisplayConfig] = useState(null);
  const [syncingTechnicalDebt, setSyncingTechnicalDebt] = useState(false);
  const [syncingWorkItems, setSyncingWorkItems] = useState(false);
  const [syncingTestCase, setSyncingTestCase] = useState(false);
  const [isFullImportFromQueriesRunning, setIsFullImportFromQueriesRunning] = useState(false);
  const [fullImportFromQueriesPollingLogId, setFullImportFromQueriesPollingLogId] = useState(null);
  const [fullImportFromQueriesResult, setFullImportFromQueriesResult] = useState(null);
  const [workItemsSyncResult, setWorkItemsSyncResult] = useState(null);
  const [testCaseSyncResult, setTestCaseSyncResult] = useState(null);
  const [rcaSyncResult, setRcaSyncResult] = useState(null);
  const [technicalDebtSyncResult, setTechnicalDebtSyncResult] = useState(null);
  const [testImportingWorkItems, setTestImportingWorkItems] = useState(false);
  const [testImportingTestCase, setTestImportingTestCase] = useState(false);
  const [testImportingRCA, setTestImportingRCA] = useState(false);
  const [testImportingTechnicalDebt, setTestImportingTechnicalDebt] = useState(false);
  const [fullImportConfigError, setFullImportConfigError] = useState(null);
  const [fullImportConfigSuccess, setFullImportConfigSuccess] = useState(null);

  // Non-ADO Imports tab
  const [nonAdoConfig, setNonAdoConfig] = useState(null);
  const [loadingNonAdoConfig, setLoadingNonAdoConfig] = useState(false);
  const [savingNonAdoConfig, setSavingNonAdoConfig] = useState(false);

  // Load initial data
  useEffect(() => {
    loadImportStats();
    testConnection();
    loadImportHistory();
    detectRunningImport();
    loadAdoConfig();
    loadDeltaImportStatus();
    loadTestImportStatus();
    loadProcessingConfig();
  }, []);

  useEffect(() => {
    if (tabValue === 3) loadBackups();
  }, [tabValue]);

  useEffect(() => {
    if (tabValue === 4) loadNonAdoImportsConfig();
  }, [tabValue]);

  const loadNonAdoImportsConfig = async () => {
    try {
      setLoadingNonAdoConfig(true);
      const response = await authGet('/api/admin/non-ado-imports-config');
      if (response.success) {
        setNonAdoConfig({
          repositorySyncEnabled: response.repositorySyncEnabled !== false,
          snykEnabled: response.snykEnabled !== false,
          datadogEnabled: response.datadogEnabled !== false,
          accessibilityEnabled: response.accessibilityEnabled !== false
        });
      }
    } catch (err) {
      console.error('Error loading non-ADO imports config:', err);
    } finally {
      setLoadingNonAdoConfig(false);
    }
  };

  const handleNonAdoToggle = async (key, value) => {
    if (!nonAdoConfig || savingNonAdoConfig) return;
    const next = { ...nonAdoConfig, [key]: value };
    setNonAdoConfig(next);
    try {
      setSavingNonAdoConfig(true);
      await authPut('/api/admin/non-ado-imports-config', next);
    } catch (err) {
      console.error('Error updating non-ADO imports config:', err);
      setNonAdoConfig(nonAdoConfig);
    } finally {
      setSavingNonAdoConfig(false);
    }
  };

  // Load Full Import (4 WIQL) config when Full Import tab is selected
  const fetchFullImportConfig = async () => {
    try {
      setLoadingFullImportConfig(true);
      setFullImportConfigError(null);
      const response = await authGet('/api/admin/engineering-processes-config');
      if (response.success) {
        setWorkItemsWiqlQuery(response.data.workItemsWiqlQuery?.trim() || DEFAULT_FULL_IMPORT_WIQL.workItems);
        const savedTestCase = response.data.testCaseWiqlQuery?.trim() || '';
        const testCaseToShow = !savedTestCase || savedTestCase === LEGACY_TEST_CASE_WIQL
          ? DEFAULT_FULL_IMPORT_WIQL.testCase
          : savedTestCase;
        setTestCaseWiqlQuery(testCaseToShow);
        setRcaWiqlQuery(response.data.rcaWiqlQuery?.trim() || DEFAULT_FULL_IMPORT_WIQL.rca);
        setTechnicalDebtWiqlQuery(response.data.technicalDebtWiqlQuery?.trim() || DEFAULT_FULL_IMPORT_WIQL.technicalDebt);
        setLastRcaSync(response.data.lastRcaSync);
        setLastTechnicalDebtSync(response.data.lastTechnicalDebtSync);
        setLastFullImport(response.data.lastFullImport);
      } else {
        setFullImportConfigError('Failed to load configuration');
      }
    } catch (err) {
      console.error('Error fetching Full Import config:', err);
      setFullImportConfigError('Failed to load configuration');
    } finally {
      setLoadingFullImportConfig(false);
    }
  };

  useEffect(() => {
    if (tabValue === 1) fetchFullImportConfig();
  }, [tabValue]);

  useEffect(() => {
    if (tabValue !== 1) return;
    let cancelled = false;
    newImportService.getFullImportDisplayConfig()
      .then((res) => {
        if (!cancelled && res?.success && res?.data) setFullImportDisplayConfig(res.data);
      })
      .catch(() => { if (!cancelled) setFullImportDisplayConfig(null); });
    return () => { cancelled = true; };
  }, [tabValue]);

  const handleSaveFullImportConfig = async () => {
    try {
      setSavingFullImportConfig(true);
      setFullImportConfigError(null);
      setFullImportConfigSuccess(null);
      const response = await authPut('/api/admin/engineering-processes-config', {
        workItemsWiqlQuery: workItemsWiqlQuery.trim(),
        testCaseWiqlQuery: testCaseWiqlQuery.trim(),
        rcaWiqlQuery: rcaWiqlQuery.trim(),
        technicalDebtWiqlQuery: technicalDebtWiqlQuery.trim(),
      });
      if (response.success) {
        setFullImportConfigSuccess('Configuration saved successfully');
        setTimeout(() => setFullImportConfigSuccess(null), 5000);
        await fetchFullImportConfig();
      } else {
        setFullImportConfigError(response.message || 'Failed to save configuration');
      }
    } catch (err) {
      setFullImportConfigError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSavingFullImportConfig(false);
    }
  };

  const handleFullImportTabSyncWorkItems = async () => {
    try {
      setSyncingWorkItems(true);
      setWorkItemsSyncResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/sync-work-items');
      if (response.success) {
        setWorkItemsSyncResult({ success: true, message: response.message, processed: response.processed, created: response.created, updated: response.updated, failed: response.failed });
      } else {
        setWorkItemsSyncResult({ success: false, message: response.message || 'Failed to sync Work Items' });
      }
    } catch (err) {
      setWorkItemsSyncResult({ success: false, message: err.response?.data?.message || 'Failed to sync Work Items' });
    } finally {
      setSyncingWorkItems(false);
    }
  };

  const handleFullImportTabSyncTestCase = async () => {
    try {
      setSyncingTestCase(true);
      setTestCaseSyncResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/sync-test-case');
      if (response.success) {
        setTestCaseSyncResult({ success: true, message: response.message, processed: response.processed, created: response.created, updated: response.updated, failed: response.failed });
      } else {
        setTestCaseSyncResult({ success: false, message: response.message || 'Failed to sync Test Case' });
      }
    } catch (err) {
      setTestCaseSyncResult({ success: false, message: err.response?.data?.message || 'Failed to sync Test Case' });
    } finally {
      setSyncingTestCase(false);
    }
  };

  const handleFullImportTabSyncRCA = async () => {
    try {
      setSyncingRCA(true);
      setRcaSyncResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/sync-rca');
      if (response.success) {
        setRcaSyncResult({ success: true, message: response.message, processed: response.processed, created: response.created, updated: response.updated, errors: response.errors });
        await fetchFullImportConfig();
      } else {
        setRcaSyncResult({ success: false, message: response.message || 'Failed to sync RCA items' });
      }
    } catch (err) {
      setRcaSyncResult({ success: false, message: err.response?.data?.message || 'Failed to sync RCA items' });
    } finally {
      setSyncingRCA(false);
    }
  };

  const handleFullImportTabSyncTechnicalDebt = async () => {
    try {
      setSyncingTechnicalDebt(true);
      setTechnicalDebtSyncResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/sync-technical-debt');
      if (response.success) {
        setTechnicalDebtSyncResult({ success: true, message: response.message, processed: response.processed, created: response.created, updated: response.updated, errors: response.errors });
        await fetchFullImportConfig();
      } else {
        setTechnicalDebtSyncResult({ success: false, message: response.message || 'Failed to sync Technical Debt items' });
      }
    } catch (err) {
      setTechnicalDebtSyncResult({ success: false, message: err.response?.data?.message || 'Failed to sync Technical Debt items' });
    } finally {
      setSyncingTechnicalDebt(false);
    }
  };

  const handleRunTestImportWorkItems = async () => {
    try {
      setTestImportingWorkItems(true);
      setWorkItemsSyncResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/run-test-import/work-items', { limit: 100 });
      if (response.success) {
        setWorkItemsSyncResult({ success: true, message: response.message, processed: response.processed, created: response.created, updated: response.updated, failed: response.failed });
      } else {
        setWorkItemsSyncResult({ success: false, message: response.message || 'Test import failed' });
      }
    } catch (err) {
      setWorkItemsSyncResult({ success: false, message: err.response?.data?.message || 'Test import failed' });
    } finally {
      setTestImportingWorkItems(false);
    }
  };

  const handleRunTestImportTestCase = async () => {
    try {
      setTestImportingTestCase(true);
      setTestCaseSyncResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/run-test-import/test-case', { limit: 100 });
      if (response.success) {
        setTestCaseSyncResult({ success: true, message: response.message, processed: response.processed, created: response.created, updated: response.updated, failed: response.failed });
      } else {
        setTestCaseSyncResult({ success: false, message: response.message || 'Test import failed' });
      }
    } catch (err) {
      setTestCaseSyncResult({ success: false, message: err.response?.data?.message || 'Test import failed' });
    } finally {
      setTestImportingTestCase(false);
    }
  };

  const handleRunTestImportRCA = async () => {
    try {
      setTestImportingRCA(true);
      setRcaSyncResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/run-test-import/rca', { limit: 100 });
      if (response.success) {
        setRcaSyncResult({ success: true, message: response.message, processed: response.processed, created: response.created, updated: response.updated, errors: response.errors });
        await fetchFullImportConfig();
      } else {
        setRcaSyncResult({ success: false, message: response.message || 'Test import failed' });
      }
    } catch (err) {
      setRcaSyncResult({ success: false, message: err.response?.data?.message || 'Test import failed' });
    } finally {
      setTestImportingRCA(false);
    }
  };

  const handleRunTestImportTechnicalDebt = async () => {
    try {
      setTestImportingTechnicalDebt(true);
      setTechnicalDebtSyncResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/run-test-import/technical-debt', { limit: 100 });
      if (response.success) {
        setTechnicalDebtSyncResult({ success: true, message: response.message, processed: response.processed, created: response.created, updated: response.updated, errors: response.errors });
        await fetchFullImportConfig();
      } else {
        setTechnicalDebtSyncResult({ success: false, message: response.message || 'Test import failed' });
      }
    } catch (err) {
      setTechnicalDebtSyncResult({ success: false, message: err.response?.data?.message || 'Test import failed' });
    } finally {
      setTestImportingTechnicalDebt(false);
    }
  };

  const handleRunFullImportFromQueries = async () => {
    try {
      setIsFullImportFromQueriesRunning(true);
      setFullImportFromQueriesResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/run-full-import');
      if (response.success && response.data?.importLogId) {
        setFullImportFromQueriesPollingLogId(response.data.importLogId);
        setFullImportFromQueriesResult({ success: true, message: 'Full import started. Check import logs for progress.' });
      } else {
        setFullImportFromQueriesResult({ success: false, message: response.message || 'Failed to start full import' });
        setIsFullImportFromQueriesRunning(false);
      }
    } catch (err) {
      setFullImportFromQueriesResult({ success: false, message: err.response?.data?.message || 'Failed to start full import' });
      setIsFullImportFromQueriesRunning(false);
    }
  };

  const handleRunTestImport = async () => {
    try {
      setIsFullImportFromQueriesRunning(true);
      setFullImportFromQueriesResult(null);
      setFullImportConfigError(null);
      const response = await authPost('/api/admin/engineering-processes/run-test-import');
      if (response.success && response.data?.importLogId) {
        setFullImportFromQueriesPollingLogId(response.data.importLogId);
        setFullImportFromQueriesResult({ success: true, message: 'Test import started (last 2 months for Work Items & Tests, all for RCA & Technical Debt, hierarchy build). Check import logs for progress.' });
      } else {
        setFullImportFromQueriesResult({ success: false, message: response.message || 'Failed to start test import' });
        setIsFullImportFromQueriesRunning(false);
      }
    } catch (err) {
      setFullImportFromQueriesResult({ success: false, message: err.response?.data?.message || 'Failed to start test import' });
      setIsFullImportFromQueriesRunning(false);
    }
  };

  const loadDefaultFullImportWiql = () => {
    setWorkItemsWiqlQuery(DEFAULT_FULL_IMPORT_WIQL.workItems);
    setTestCaseWiqlQuery(DEFAULT_FULL_IMPORT_WIQL.testCase);
    setRcaWiqlQuery(DEFAULT_FULL_IMPORT_WIQL.rca);
    setTechnicalDebtWiqlQuery(DEFAULT_FULL_IMPORT_WIQL.technicalDebt);
    setFullImportConfigSuccess('Default WIQL loaded. Save to persist.');
    setTimeout(() => setFullImportConfigSuccess(null), 3000);
  };

  const hasAnyFullImportQuery = workItemsWiqlQuery.trim() || testCaseWiqlQuery.trim() || technicalDebtWiqlQuery.trim() || rcaWiqlQuery.trim();

  // Poll full import (4 WIQL) status when running
  useEffect(() => {
    if (!fullImportFromQueriesPollingLogId) return;
    const interval = setInterval(async () => {
      try {
        const response = await authGet(`/api/new-import/logs/${fullImportFromQueriesPollingLogId}`);
        if (response.data && response.data.status !== 'running') {
          setFullImportFromQueriesPollingLogId(null);
          setIsFullImportFromQueriesRunning(false);
          await fetchFullImportConfig();
          const details = response.data.details || {};
          const msg = response.data.status === 'failed'
            ? (response.data.error || 'Import failed')
            : `Import completed. Work Items: ${details.workItems?.created ?? 0} created, ${details.workItems?.updated ?? 0} updated; Tests: ${details.testCase?.created ?? 0} created; Hierarchies: ${details.hierarchies ?? 0} built.`;
          setFullImportFromQueriesResult({ success: response.data.status !== 'failed', message: msg });
        }
      } catch (e) {
        setFullImportFromQueriesPollingLogId(null);
        setIsFullImportFromQueriesRunning(false);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [fullImportFromQueriesPollingLogId]);
  
  const loadDeltaImportStatus = async () => {
    try {
      const response = await newImportService.getDeltaImportStatus();
      if (response.success && response.data) {
        setDeltaImportConfig(response.data);
      }
    } catch (err) {
      console.error('Failed to load delta import status:', err);
    }
  };
  
  const loadTestImportStatus = async () => {
    try {
      const response = await newImportService.getTestImportStatus();
      if (response.success && response.data) {
        setTestDeltaImportConfig({
          cronSchedule: response.data.cronSchedule || '0 */2 * * *',
          hoursBack: response.data.hoursBack || 3,
          lastRun: response.data.lastRun,
          nextRun: response.data.nextRun
        });
        // Preserve discoveredFields if they exist, only update queryUrl
        setTestConfig(prev => ({
          ...prev,
          queryUrl: response.data.queryUrl || prev.queryUrl || ''
        }));
      }
    } catch (err) {
      console.error('Failed to load test import status:', err);
    }
  };

  const loadAdoConfig = async () => {
    try {
      const response = await newImportService.getAdoConfig();
      if (response.success && response.data) {
        setAdoConfig(response.data);
      }
    } catch (err) {
      console.error('Failed to load ADO config:', err);
    }
  };

  const loadProcessingConfig = async () => {
    try {
      const response = await fetch('/api/processing-config');
      const data = await response.json();
      if (data.success) {
        setProcessingConfig(data.data);

        // Event-based: Delta Imports tab content is readonly; no redirect needed
      }
    } catch (error) {
      console.error('Error loading processing config:', error);
    }
  };

  // Check if event processing is enabled (webhook enabled and batch disabled)
  const isEventProcessingEnabled = processingConfig &&
    processingConfig.webhookEnabled &&
    !processingConfig.batchEnabled;

  const handleUpdateAdoConfig = async () => {
    try {
      const payload = { ...adoConfig };
      delete payload.additionalPatsCount;
      const response = await newImportService.updateAdoConfig(payload);
      if (response.success) {
        setSuccess('ADO configuration updated successfully');
        setError('');
        if (response.data) setAdoConfig((prev) => ({ ...prev, ...response.data }));
      } else {
        setError(response.message || 'Failed to update ADO configuration');
      }
    } catch (err) {
      setError(`Failed to update ADO configuration: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleDiscoverFields = async () => {
    try {
      setIsDiscoveringFields(true);
      setError('');
      setSuccess('');
      
      const response = await newImportService.discoverFieldsFromQuery();
      if (response.success && response.data) {
        setDiscoveredFields(response.data);
        setSuccess(response.data.message || `Discovered ${response.data.fields?.length || 0} fields`);
      } else {
        setError(response.message || 'Failed to discover fields');
      }
    } catch (err) {
      setError(`Failed to discover fields: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsDiscoveringFields(false);
    }
  };

  const handleMonthlyBatchImport = async () => {
    try {
      setIsMonthlyImportRunning(true);
      setError('');
      setSuccess('');

      if (onImportStart) {
        onImportStart('Monthly Batch Import');
      }

      const response = await newImportService.runMonthlyBatchImport();

      if (response.success) {
        setSuccess('Monthly batch import started in background. Importing from Jan 1, 2025 in monthly batches.');
        if (response.data?.importLogId) {
          setPollingLogId(response.data.importLogId);
        }
        loadImportHistory();
      } else {
        setError(response.message || 'Failed to start monthly batch import');
        setIsMonthlyImportRunning(false);
      }
    } catch (err) {
      setError(`Failed to start monthly batch import: ${err.response?.data?.message || err.message}`);
      setIsMonthlyImportRunning(false);
    }
  };

  const handleBuildHierarchies = async () => {
    try {
      setIsBuildingHierarchies(true);
      setError('');
      setSuccess('');

      const response = await newImportService.buildHierarchies();

      if (response.success) {
        const hierarchyCount = response.data?.hierarchiesBuilt || response.data?.hierarchyCount || 0;
        setSuccess(`Hierarchies built successfully. ${hierarchyCount} hierarchies created.`);
        loadImportStats(); // Refresh stats after building hierarchies
      } else {
        setError(response.message || 'Failed to build hierarchies');
      }
    } catch (err) {
      setError(`Failed to build hierarchies: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsBuildingHierarchies(false);
    }
  };

  const handleRebuildHierarchies = async () => {
    try {
      setIsRebuildingHierarchies(true);
      setError('');
      setSuccess('');
      const response = await newImportService.rebuildHierarchies();
      if (response.success) {
        const count = response.data?.hierarchyCount ?? response.data?.hierarchiesBuilt ?? '';
        setSuccess(`Hierarchies rebuilt from WorkItem collection.${count ? ` ${count} hierarchies.` : ''}`);
        loadImportStats();
      } else {
        setError(response.message || 'Failed to rebuild hierarchies');
      }
    } catch (err) {
      setError(`Failed to rebuild hierarchies: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsRebuildingHierarchies(false);
    }
  };

  // Poll for status every 3 seconds when import is running
  useEffect(() => {
    if (!pollingLogId) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await newImportService.pollImportStatus(pollingLogId);
        if (response.data.status !== 'running') {
          const importType = response.data.type || 'Import';
          const isSuccess = response.data.status === 'success' || response.data.status === 'partial';
          const totalItems = response.data.details?.total?.total || response.data.details?.total || 0;
          const created = response.data.details?.total?.created || response.data.details?.created || 0;
          const updated = response.data.details?.total?.updated || response.data.details?.updated || 0;
          
          // Reset all running states
          setPollingLogId(null);
          setIsFullImportRunning(false);
          setIsDeltaImportRunning(false);
          setIsImportAfterDateRunning(false);
          setIsMonthlyImportRunning(false);
          setIsTestMonthlyImportRunning(false);
          setIsTestDeltaImportRunning(false);
          setIsTestImportAfterDateRunning(false);
          
          // Reload data
          loadImportHistory();
          loadImportStats();
          
          // Build success message
          let successMessage = '';
          if (response.data.type === 'monthly_batch' && response.data.details?.hierarchies) {
            successMessage = `Import completed. Built ${response.data.details.hierarchies} hierarchies.`;
          } else if (isSuccess) {
            successMessage = `Import completed successfully. Created: ${created}, Updated: ${updated}, Total: ${totalItems}`;
          } else {
            successMessage = `Import failed: ${response.data.error || 'Unknown error'}`;
          }
          
          setSuccess(successMessage);
          
          // Notify parent component
          if (onImportComplete) {
            onImportComplete(importType, {
              ...response.data,
              success: isSuccess,
              total: totalItems,
              created,
              updated
            });
          }
          // Data already refreshed via loadImportHistory/loadImportStats – user stays on same page/tab
        }
      } catch (error) {
        console.error('Error polling import status:', error);
        setPollingLogId(null);
        setIsFullImportRunning(false);
        setIsDeltaImportRunning(false);
        setIsImportAfterDateRunning(false);
        setIsMonthlyImportRunning(false);
        setIsTestMonthlyImportRunning(false);
        setIsTestDeltaImportRunning(false);
        setIsTestImportAfterDateRunning(false);
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [pollingLogId, onImportComplete]);

  const loadImportStats = async () => {
    try {
      const response = await newImportService.getImportStats();
      if (response.success && response.data) {
        setImportStats(response.data);
      }
    } catch (err) {
      console.error('Failed to load import stats:', err);
    }
  };

  const testConnection = async () => {
    try {
      setConnectionStatus('testing');
      const response = await newImportService.testConnection();
      setConnectionStatus(response.success ? 'success' : 'error');
    } catch (err) {
      setConnectionStatus('error');
      console.error('Connection test failed:', err);
    }
  };

  const loadImportHistory = async () => {
    try {
      const response = await newImportService.getImportLogs({ limit: 25 });
      if (response.success && response.data) {
        setImportHistory(response.data.logs || []);
      }
    } catch (err) {
      console.error('Failed to load import history:', err);
    }
  };

  const detectRunningImport = async () => {
    try {
      const response = await newImportService.getCurrentImportStatus();
      if (response.success && response.data && response.data.status === 'running') {
        setHasRunningImport(true);
        if (response.data.importLogId) {
          setPollingLogId(response.data.importLogId);
        }
      } else {
        setHasRunningImport(false);
      }
    } catch (err) {
      console.error('Failed to detect running import:', err);
    }
  };

  const handleWorkItemTypeChange = (type) => {
    setSelectedWorkItemTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const getSelectedTypes = () => {
    return Object.entries(selectedWorkItemTypes)
      .filter(([_, selected]) => selected)
      .map(([key, _]) => {
        const type = workItemTypes.find(t => t.key === key);
        return type ? type.label : key;
      });
  };

  const handleFullImport = async () => {
    try {
      const selectedTypes = getSelectedTypes();
      if (selectedTypes.length === 0) {
        setError('Please select at least one work item type');
        return;
      }

      setIsFullImportRunning(true);
      setError('');
      setSuccess('');

      if (onImportStart) {
        onImportStart('Full Import');
      }

      const response = await newImportService.runFullImport({
        dateFilter: '2024-01-01',
        clearExisting: true,
        workItemTypes: selectedTypes.length > 0 ? selectedTypes : undefined
      });

      if (response.success) {
        setSuccess('Full import started in background. You can navigate away and check back later.');
        if (response.data?.importLogId) {
          setPollingLogId(response.data.importLogId);
        }
        loadImportHistory();
      } else {
        setError(response?.message || 'Failed to start full import');
        setIsFullImportRunning(false);
      }
    } catch (err) {
      setError(`Failed to start full import: ${err.response?.data?.message || err.message}`);
      setIsFullImportRunning(false);
      console.error('Failed to start full import:', err);
    }
  };

  const handleDeltaImport = async () => {
    try {
      setIsDeltaImportRunning(true);
      setError('');
      setSuccess('');

      if (onImportStart) {
        onImportStart('Delta Import');
      }

      const response = await newImportService.runDeltaImport({
        hoursBack: deltaImportConfig.hoursBack || 3
      });

      if (response.success) {
        setSuccess('Delta import started in background. You can navigate away and check back later.');
        if (response.data?.importLogId) {
          setPollingLogId(response.data.importLogId);
        }
        loadDeltaImportStatus();
        loadImportHistory();
      } else {
        setError(response?.message || 'Failed to start delta import');
        setIsDeltaImportRunning(false);
      }
    } catch (err) {
      setError(`Failed to start delta import: ${err.response?.data?.message || err.message}`);
      setIsDeltaImportRunning(false);
      console.error('Failed to start delta import:', err);
    }
  };
  
  const handleSaveDeltaConfig = async () => {
    try {
      setIsSavingDeltaConfig(true);
      setError('');
      setSuccess('');
      
      const response = await newImportService.updateDeltaImportConfig({
        cronSchedule: deltaImportConfig.cronSchedule,
        hoursBack: deltaImportConfig.hoursBack
      });
      
      if (response.success) {
        setSuccess('Delta import configuration saved successfully');
        await loadDeltaImportStatus();
      } else {
        setError(response.message || 'Failed to save delta import configuration');
      }
    } catch (err) {
      setError(`Failed to save delta import configuration: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSavingDeltaConfig(false);
    }
  };

  /** Save the single schedule to both Work Items and Tests delta configs */
  const handleSaveScheduleAll = async () => {
    try {
      setIsSavingDeltaConfig(true);
      setError('');
      setSuccess('');
      const cronSchedule = deltaImportConfig.cronSchedule || '0 */2 * * *';
      const hoursBack = deltaImportConfig.hoursBack ?? 3;
      const [workItemsRes, testsRes] = await Promise.all([
        newImportService.updateDeltaImportConfig({ cronSchedule, hoursBack }),
        newImportService.updateTestImportConfig({ cronSchedule, hoursBack })
      ]);
      if (workItemsRes.success && testsRes.success) {
        setSuccess('Schedule saved for Work Items and Tests');
        await Promise.all([loadDeltaImportStatus(), loadTestImportStatus()]);
        setTestDeltaImportConfig(prev => ({ ...prev, cronSchedule, hoursBack }));
      } else {
        setError(workItemsRes.success ? (testsRes.message || 'Failed to save test schedule') : (workItemsRes.message || 'Failed to save work items schedule'));
      }
    } catch (err) {
      setError(`Failed to save schedule: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSavingDeltaConfig(false);
    }
  };

  const loadBackups = async () => {
    try {
      const res = await backupRestoreService.listBackups();
      if (res.success && Array.isArray(res.data)) {
        setBackups(res.data);
        if (!res.data.some(b => b.name === selectedBackup)) setSelectedBackup('');
      }
    } catch (err) {
      console.error('Failed to list backups:', err);
      setBackups([]);
    }
  };

  const handleCreateBackup = async () => {
    const name = (backupName || '').trim();
    if (!name) {
      setError('Please enter a backup name.');
      return;
    }
    try {
      setIsBackupRunning(true);
      setError('');
      setSuccess('');
      const res = await backupRestoreService.createBackup(name);
      if (res.success) {
        setSuccess(`Backup "${res.data?.name || name}" created successfully.`);
        setBackupName('');
        await loadBackups();
      } else {
        setError(res.message || 'Failed to create backup');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create backup');
    } finally {
      setIsBackupRunning(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) {
      setError('Please select a backup to restore.');
      return;
    }
    try {
      setIsRestoreRunning(true);
      setError('');
      setSuccess('');
      const res = await backupRestoreService.restoreBackup(selectedBackup);
      if (res.success) {
        setSuccess('Database restored successfully. Refreshing the page may be required.');
        await loadBackups();
      } else {
        setError(res.message || 'Failed to restore backup');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to restore backup');
    } finally {
      setIsRestoreRunning(false);
    }
  };
  
  // Test import handlers
  const handleUpdateTestConfig = async () => {
    try {
      setIsSavingTestConfig(true);
      setError('');
      setSuccess('');
      
      const queryUrlToSave = testConfig.queryUrl;
      
      const response = await newImportService.updateTestImportConfig({
        queryUrl: queryUrlToSave
      });
      
      if (response.success) {
        setSuccess('Test configuration updated successfully');
        // Update state with the saved queryUrl from response or keep the one we just saved
        const savedQueryUrl = response.data?.queryUrl || queryUrlToSave;
        setTestConfig(prev => ({
          ...prev,
          queryUrl: savedQueryUrl
        }));
        // Reload test import status to get updated config (cron, lastRun, etc.)
        await loadTestImportStatus();
      } else {
        setError(response.message || 'Failed to update test configuration');
      }
    } catch (err) {
      setError(`Failed to update test configuration: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSavingTestConfig(false);
    }
  };
  
  const handleDiscoverTestFields = async () => {
    try {
      setIsDiscoveringTestFields(true);
      setError('');
      setSuccess('');
      
      const response = await newImportService.discoverTestFieldsFromQuery();
      if (response.success && response.data) {
        setTestConfig(prev => ({
          ...prev,
          discoveredFields: response.data.fields || []
        }));
        setSuccess(response.data.message || `Discovered ${response.data.fields?.length || 0} fields`);
      } else {
        setError(response.message || 'Failed to discover test fields');
      }
    } catch (err) {
      setError(`Failed to discover test fields: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsDiscoveringTestFields(false);
    }
  };
  
  const handleTestMonthlyBatchImport = async () => {
    try {
      setIsTestMonthlyImportRunning(true);
      setError('');
      setSuccess('');

      if (onImportStart) {
        onImportStart('Test Monthly Batch Import');
      }

      const response = await newImportService.runTestMonthlyBatchImport();

      if (response.success) {
        setSuccess('Test monthly batch import started in background. Importing from Jan 1, 2018 in monthly batches.');
        if (response.data?.importLogId) {
          setPollingLogId(response.data.importLogId);
        }
        loadImportHistory();
      } else {
        setError(response.message || 'Failed to start test monthly batch import');
        setIsTestMonthlyImportRunning(false);
      }
    } catch (err) {
      setError(`Failed to start test monthly batch import: ${err.response?.data?.message || err.message}`);
      setIsTestMonthlyImportRunning(false);
    }
  };
  
  const handleTestDeltaImport = async () => {
    try {
      setIsTestDeltaImportRunning(true);
      setError('');
      setSuccess('');

      if (onImportStart) {
        onImportStart('Test Delta Import');
      }

      const response = await newImportService.runTestDeltaImport({
        hoursBack: testDeltaImportConfig.hoursBack || 3
      });

      if (response.success) {
        setSuccess('Test delta import started in background. You can navigate away and check back later.');
        if (response.data?.importLogId) {
          setPollingLogId(response.data.importLogId);
        }
        loadTestImportStatus();
        loadImportHistory();
      } else {
        setError(response?.message || 'Failed to start test delta import');
        setIsTestDeltaImportRunning(false);
      }
    } catch (err) {
      setError(`Failed to start test delta import: ${err.response?.data?.message || err.message}`);
      setIsTestDeltaImportRunning(false);
      console.error('Failed to start test delta import:', err);
    }
  };

  /** Run both work items and tests delta with the same hours (Run Delta section). Uses selected work item types. */
  const handleRunDelta = async () => {
    const selectedTypes = getSelectedTypes();
    if (selectedTypes.length === 0) {
      setError('Please select at least one work item type');
      return;
    }
    try {
      setIsDeltaImportRunning(true);
      setIsTestDeltaImportRunning(true);
      setError('');
      setSuccess('');

      if (onImportStart) {
        onImportStart('Delta Import');
      }

      const hours = Math.min(24, Math.max(1, runDeltaHoursBack || 4));
      const [workItemsRes, testsRes] = await Promise.all([
        newImportService.runDeltaImport({ hoursBack: hours, workItemTypes: selectedTypes }),
        newImportService.runTestDeltaImport({ hoursBack: hours })
      ]);

      if (workItemsRes.success && testsRes.success) {
        setSuccess('Work items and tests delta import started. You can navigate away and check back later.');
        if (workItemsRes.data?.importLogId) {
          setPollingLogId(workItemsRes.data.importLogId);
        }
        loadDeltaImportStatus();
        loadTestImportStatus();
        loadImportHistory();
      } else {
        const msg = !workItemsRes.success
          ? (workItemsRes?.message || 'Failed to start work items delta')
          : (testsRes?.message || 'Failed to start tests delta');
        setError(msg);
        setIsDeltaImportRunning(false);
        setIsTestDeltaImportRunning(false);
      }
    } catch (err) {
      setError(`Run delta failed: ${err.response?.data?.message || err.message}`);
      console.error('Run delta failed:', err);
      setIsDeltaImportRunning(false);
      setIsTestDeltaImportRunning(false);
    }
  };
  
  const handleTestImportAfterDate = async () => {
    try {
      if (!testSelectedDate) {
        setError('Please select a date');
        return;
      }

      setIsTestImportAfterDateRunning(true);
      setError('');
      setSuccess('');

      if (onImportStart) {
        onImportStart('Test Import After Date');
      }

      const response = await newImportService.runTestImportAfterDate(testSelectedDate);

      if (response.success) {
        setSuccess('Test import after date started in background. You can navigate away and check back later.');
        if (response.data?.importLogId) {
          setPollingLogId(response.data.importLogId);
        }
        loadImportHistory();
      } else {
        setError(response?.message || 'Failed to start test import after date');
        setIsTestImportAfterDateRunning(false);
      }
    } catch (err) {
      setError(`Failed to start test import after date: ${err.response?.data?.message || err.message}`);
      setIsTestImportAfterDateRunning(false);
      console.error('Failed to start test import after date:', err);
    }
  };
  
  const handleSaveTestDeltaConfig = async () => {
    try {
      setIsSavingTestConfig(true);
      setError('');
      setSuccess('');
      
      const response = await newImportService.updateTestImportConfig({
        cronSchedule: testDeltaImportConfig.cronSchedule,
        hoursBack: testDeltaImportConfig.hoursBack
      });
      
      if (response.success) {
        setSuccess('Test delta import configuration saved successfully');
        await loadTestImportStatus();
      } else {
        setError(response.message || 'Failed to save test delta import configuration');
      }
    } catch (err) {
      setError(`Failed to save test delta import configuration: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSavingTestConfig(false);
    }
  };
  
  // Helper to convert hours to cron expression
  const hoursToCron = (hours) => {
    if (hours <= 0) return '0 */2 * * *';
    return `0 */${hours} * * *`;
  };
  
  // Helper to parse cron to hours
  const cronToHours = (cron) => {
    const match = cron.match(/0 \*\/(\d+) \* \* \*/);
    return match ? parseInt(match[1]) : 2;
  };

  const handleImportAfterDate = async () => {
    try {
      if (!selectedDate) {
        setError('Please select a date');
        return;
      }

      const selectedTypes = getSelectedTypes();
      if (selectedTypes.length === 0) {
        setError('Please select at least one work item type');
        return;
      }

      setIsImportAfterDateRunning(true);
      setError('');
      setSuccess('');

      if (onImportStart) {
        onImportStart('Import After Date');
      }

      const response = await newImportService.importAfterDate(selectedDate, {
        workItemTypes: selectedTypes.length > 0 ? selectedTypes : undefined
      });

      if (response.success || response.importLogId) {
        setSuccess('Import after date started in background. You can navigate away and check back later.');
        if (response.importLogId || response.data?.importLogId) {
          setPollingLogId(response.importLogId || response.data?.importLogId);
        }
        loadImportHistory();
      } else {
        setError(response?.message || 'Failed to start import after date');
        setIsImportAfterDateRunning(false);
      }
    } catch (err) {
      setError(`Failed to start import after date: ${err.response?.data?.message || err.message}`);
      setIsImportAfterDateRunning(false);
      console.error('Failed to start import after date:', err);
    }
  };

  const isAnyImportRunning = () => {
    return isFullImportRunning || isDeltaImportRunning || isImportAfterDateRunning || isMonthlyImportRunning ||
           isFullImportFromQueriesRunning || fullImportFromQueriesPollingLogId ||
           isTestMonthlyImportRunning || isTestDeltaImportRunning || isTestImportAfterDateRunning ||
           pollingLogId || hasRunningImport;
  };

  const getConnectionStatusText = () => {
    if (connectionStatus === 'testing') return 'Testing...';
    if (connectionStatus === 'success') return 'Connected';
    if (connectionStatus === 'error') return 'Connection Failed';
    return 'Not Tested';
  };

  const getConnectionStatusColor = () => {
    if (connectionStatus === 'success') return 'success';
    if (connectionStatus === 'error') return 'error';
    return 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Box>
      {/* Connection Status */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Connection Status</Typography>
            <Chip 
              label={getConnectionStatusText()} 
              color={getConnectionStatusColor()}
              size="small"
            />
          </Box>
          <Tooltip title="Test connection uses your profile ADO PAT if set, otherwise the Admin Access Tokens pool. Test Import uses the same.">
            <IconButton onClick={testConnection} disabled={connectionStatus === 'testing'}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Connection and Test Import use your profile ADO PAT (Profile) if set; otherwise the Admin → Access Tokens pool.
        </Typography>
      </Paper>


      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Import Actions with Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Import Actions
          </Typography>

          {isEventProcessingEnabled && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Event-Based Processing is enabled.</strong> Delta Imports is read-only as data is processed in real-time through webhooks.
              </Typography>
            </Alert>
          )}

          <ConsoleTabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label="Processing Type" />
            <Tab label="Full Import" />
            <Tab label="Delta Imports" />
            <Tab label="Backup & Restore" />
            <Tab label="Non-ADO Imports" />
          </ConsoleTabs>

          {/* Processing Type Tab */}
          {tabValue === 0 && (
            <Box>
              <ProcessingConfig onConfigChange={loadProcessingConfig} />

              {/* Rebuild Hierarchies */}
              <Card variant="outlined" sx={{ mt: 3, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Rebuild Hierarchies
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manually rebuild epic hierarchies from the WorkItem collection. This is automatically done after imports.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<BuildIcon />}
                    onClick={handleRebuildHierarchies}
                    disabled={isRebuildingHierarchies || isAnyImportRunning()}
                  >
                    {isRebuildingHierarchies ? 'Rebuilding...' : 'Rebuild Hierarchies'}
                  </Button>
                </Box>
                {isRebuildingHierarchies && <LinearProgress sx={{ mt: 2 }} />}
              </Card>

              {/* Update after a date widget */}
              <Card variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Update after a date
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Import work items (selected types in Delta Imports) changed or created after the selected date.
                </Typography>
                <TextField
                  label="Select Date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  fullWidth
                  sx={{ mb: 2, maxWidth: 280 }}
                  InputLabelProps={{ shrink: true }}
                  disabled={isAnyImportRunning() || connectionStatus !== 'success'}
                />
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<CalendarToday />}
                  onClick={handleImportAfterDate}
                  disabled={isAnyImportRunning() || connectionStatus !== 'success' || !selectedDate}
                >
                  {isImportAfterDateRunning ? 'Starting...' : 'Import Work Items After Date'}
                </Button>
                {isImportAfterDateRunning && <LinearProgress sx={{ mt: 2 }} />}
              </Card>

              {/* Data Statistics */}
              {importStats && (
                <Paper sx={{ p: 2, mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Data Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {importStats.total || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Work Items
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="secondary">
                          {importStats.workItemCounts?.epics || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Epics
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">
                          {importStats.workItemCounts?.features || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Features
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          {importStats.workItemCounts?.tests || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tests
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">
                          {importStats.workItemCounts?.bugs || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Bugs
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">
                          {importStats.workItemCounts?.productBacklogItems || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          PBIs
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="secondary">
                          {importStats.workItemCounts?.tasks || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tasks
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>
          )}

          {/* Full Import Tab - 4 WIQL areas (Work Items, Test Case, RCA, Technical Debt) */}
          {tabValue === 1 && (
            <Box>
              {loadingFullImportConfig && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Loading configuration...</Typography>
                </Box>
              )}
              {fullImportConfigError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFullImportConfigError(null)}>{fullImportConfigError}</Alert>
              )}
              {fullImportConfigSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFullImportConfigSuccess(null)}>{fullImportConfigSuccess}</Alert>
              )}

              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', background: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: FULL_IMPORT_DARK_GREEN, fontWeight: 'bold' }}>Full Import</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Runs all 4 areas: Work Items (Created ≥ Jan 2025, monthly batches), Test Case (same), Technical Debt, RCA. Uses PAT rotation. Builds epic hierarchies at the end.
                    </Typography>
                    {lastFullImport && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>Last run: {format(new Date(lastFullImport), 'MMM d, yyyy HH:mm')}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={handleRunFullImportFromQueries}
                      disabled={isFullImportFromQueriesRunning || !hasAnyFullImportQuery}
                      startIcon={isFullImportFromQueriesRunning ? <LinearProgress size={20} /> : <SyncIcon />}
                      sx={{ backgroundColor: FULL_IMPORT_DARK_GREEN, '&:hover': { backgroundColor: '#0f2e1f' } }}
                    >
                      {isFullImportFromQueriesRunning ? 'Running...' : 'Run Full Import'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleRunTestImport}
                      disabled={isFullImportFromQueriesRunning || !hasAnyFullImportQuery}
                      sx={{ borderColor: FULL_IMPORT_DARK_GREEN, color: FULL_IMPORT_DARK_GREEN, '&:hover': { borderColor: '#0f2e1f', backgroundColor: 'rgba(29, 68, 52, 0.04)' } }}
                      title="Last 2 months for Work Items & Tests, all for RCA & Technical Debt, then hierarchy build"
                    >
                      Run Test Import
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={loadDefaultFullImportWiql}
                      disabled={loadingFullImportConfig}
                      sx={{ borderColor: 'grey.500', color: 'text.secondary', '&:hover': { borderColor: 'grey.700', backgroundColor: 'action.hover' } }}
                      title="Fill all four WIQL fields with default queries (Save to persist)"
                    >
                      Load default WIQL
                    </Button>
                  </Box>
                </Box>
                {fullImportFromQueriesResult && (
                  <Alert severity={fullImportFromQueriesResult.success ? 'success' : 'error'} sx={{ mt: 2 }} onClose={() => setFullImportFromQueriesResult(null)}>{fullImportFromQueriesResult.message}</Alert>
                )}
              </Paper>

              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', background: 'white' }}>
                <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, '& .MuiAccordionSummary-content': { my: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <SettingsIcon sx={{ color: FULL_IMPORT_DARK_GREEN, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ color: FULL_IMPORT_DARK_GREEN, fontWeight: 'bold' }}>1. Work Items (Epics, Features, PBI, Bugs, Tasks)</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, pt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={6} label="Work Items WIQL Query" value={workItemsWiqlQuery} onChange={(e) => setWorkItemsWiqlQuery(e.target.value)}
                          placeholder="SELECT [System.Id], [System.Title], ... FROM WorkItems WHERE [System.TeamProject] = 'HighQ Tech' AND [System.WorkItemType] IN ('Epic','Feature','Product Backlog Item','Bug','Task') AND [System.State] <> 'Removed' ORDER BY [System.Id]"
                          disabled={loadingFullImportConfig || savingFullImportConfig}
                          helperText="Created Date ≥ 1 Jan 2025 is applied automatically; data is fetched in monthly batches. Parent is fetched for hierarchy."
                          sx={{ '& .MuiInputBase-root': { width: '100%' } }} />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Button variant="contained" size="small" onClick={handleSaveFullImportConfig} disabled={savingFullImportConfig || loadingFullImportConfig} startIcon={savingFullImportConfig ? <LinearProgress size={20} /> : <SaveIcon />} sx={{ backgroundColor: FULL_IMPORT_DARK_GREEN, '&:hover': { backgroundColor: '#0f2e1f' } }}>{savingFullImportConfig ? 'Saving...' : 'Save Query'}</Button>
                        <Button variant="outlined" size="small" onClick={handleFullImportTabSyncWorkItems} disabled={syncingWorkItems || loadingFullImportConfig || !workItemsWiqlQuery.trim()} startIcon={syncingWorkItems ? <LinearProgress size={20} /> : <SyncIcon />} sx={{ borderColor: FULL_IMPORT_DARK_GREEN, color: FULL_IMPORT_DARK_GREEN, '&:hover': { borderColor: '#0f2e1f', backgroundColor: 'rgba(29, 68, 52, 0.04)' } }}>{syncingWorkItems ? 'Syncing...' : 'Sync Work Items'}</Button>
                        <Button variant="outlined" size="small" onClick={handleRunTestImportWorkItems} disabled={testImportingWorkItems || loadingFullImportConfig || !workItemsWiqlQuery.trim()} startIcon={testImportingWorkItems ? <LinearProgress size={20} /> : null} sx={{ borderColor: 'grey.500', color: 'text.secondary', '&:hover': { borderColor: 'grey.700', backgroundColor: 'action.hover' } }} title="Fetch and upsert first 100 records (no clear)">{testImportingWorkItems ? 'Running...' : 'Run Test Import (100)'}</Button>
                      </Grid>
                      {workItemsSyncResult && (
                        <Grid item xs={12}><Alert severity={workItemsSyncResult.success ? 'success' : 'error'} onClose={() => setWorkItemsSyncResult(null)}>{workItemsSyncResult.message}{workItemsSyncResult.success && <Typography variant="body2" sx={{ mt: 1 }}>Processed: {workItemsSyncResult.processed ?? 0} | Created: {workItemsSyncResult.created ?? 0} | Updated: {workItemsSyncResult.updated ?? 0}{workItemsSyncResult.failed != null ? ` | Failed: ${workItemsSyncResult.failed}` : ''}</Typography>}</Alert></Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', background: 'white' }}>
                <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, '& .MuiAccordionSummary-content': { my: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <SettingsIcon sx={{ color: FULL_IMPORT_DARK_GREEN, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ color: FULL_IMPORT_DARK_GREEN, fontWeight: 'bold' }}>2. Test Case</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, pt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={6} label="Test Case WIQL Query" value={testCaseWiqlQuery} onChange={(e) => setTestCaseWiqlQuery(e.target.value)}
                          placeholder="SELECT [System.Id], [System.Title], ... FROM WorkItems WHERE [System.TeamProject] = 'HighQ Tech' AND [System.WorkItemType] = 'Test Case' AND [System.State] NOT IN ('Closed', 'Done', 'Removed') ORDER BY [System.Id]"
                          disabled={loadingFullImportConfig || savingFullImportConfig}
                          helperText="Created Date from 1 Jan 2018; every year is fetched in monthly batches (2018–current)." />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Button variant="contained" size="small" onClick={handleSaveFullImportConfig} disabled={savingFullImportConfig || loadingFullImportConfig} startIcon={savingFullImportConfig ? <LinearProgress size={20} /> : <SaveIcon />} sx={{ backgroundColor: FULL_IMPORT_DARK_GREEN, '&:hover': { backgroundColor: '#0f2e1f' } }}>{savingFullImportConfig ? 'Saving...' : 'Save Query'}</Button>
                        <Button variant="outlined" size="small" onClick={handleFullImportTabSyncTestCase} disabled={syncingTestCase || loadingFullImportConfig || !testCaseWiqlQuery.trim()} startIcon={syncingTestCase ? <LinearProgress size={20} /> : <SyncIcon />} sx={{ borderColor: FULL_IMPORT_DARK_GREEN, color: FULL_IMPORT_DARK_GREEN, '&:hover': { borderColor: '#0f2e1f', backgroundColor: 'rgba(29, 68, 52, 0.04)' } }}>{syncingTestCase ? 'Syncing...' : 'Sync Test Case'}</Button>
                        <Button variant="outlined" size="small" onClick={handleRunTestImportTestCase} disabled={testImportingTestCase || loadingFullImportConfig || !testCaseWiqlQuery.trim()} startIcon={testImportingTestCase ? <LinearProgress size={20} /> : null} sx={{ borderColor: 'grey.500', color: 'text.secondary', '&:hover': { borderColor: 'grey.700', backgroundColor: 'action.hover' } }} title="Fetch and upsert first 100 records (no clear)">{testImportingTestCase ? 'Running...' : 'Run Test Import (100)'}</Button>
                      </Grid>
                      {testCaseSyncResult && (
                        <Grid item xs={12}><Alert severity={testCaseSyncResult.success ? 'success' : 'error'} onClose={() => setTestCaseSyncResult(null)}>{testCaseSyncResult.message}{testCaseSyncResult.success && <Typography variant="body2" sx={{ mt: 1 }}>Processed: {testCaseSyncResult.processed ?? 0} | Created: {testCaseSyncResult.created ?? 0} | Updated: {testCaseSyncResult.updated ?? 0}{testCaseSyncResult.failed != null ? ` | Failed: ${testCaseSyncResult.failed}` : ''}</Typography>}</Alert></Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', background: 'white' }}>
                <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, '& .MuiAccordionSummary-content': { my: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <SettingsIcon sx={{ color: FULL_IMPORT_DARK_GREEN, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ color: FULL_IMPORT_DARK_GREEN, fontWeight: 'bold' }}>3. RCA</Typography>
                      {lastRcaSync && <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>Last sync: {format(new Date(lastRcaSync), 'MMM d, yyyy HH:mm')}</Typography>}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, pt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={6} label="RCA WIQL Query" value={rcaWiqlQuery} onChange={(e) => setRcaWiqlQuery(e.target.value)}
                          placeholder="SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType] = 'RCA'" disabled={loadingFullImportConfig || savingFullImportConfig} helperText="Enter WIQL query to fetch RCA items from Azure DevOps. RCA and Technical Debt are kept in sync via Event Processing and Delta imports." />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Button variant="contained" size="small" onClick={handleSaveFullImportConfig} disabled={savingFullImportConfig || loadingFullImportConfig} startIcon={savingFullImportConfig ? <LinearProgress size={20} /> : <SaveIcon />} sx={{ backgroundColor: FULL_IMPORT_DARK_GREEN, '&:hover': { backgroundColor: '#0f2e1f' } }}>{savingFullImportConfig ? 'Saving...' : 'Save Query'}</Button>
                        <Button variant="outlined" size="small" onClick={handleFullImportTabSyncRCA} disabled={syncingRCA || loadingFullImportConfig || !rcaWiqlQuery.trim()} startIcon={syncingRCA ? <LinearProgress size={20} /> : <SyncIcon />} sx={{ borderColor: FULL_IMPORT_DARK_GREEN, color: FULL_IMPORT_DARK_GREEN, '&:hover': { borderColor: '#0f2e1f', backgroundColor: 'rgba(29, 68, 52, 0.04)' } }}>{syncingRCA ? 'Syncing...' : 'Sync RCA Items'}</Button>
                        <Button variant="outlined" size="small" onClick={handleRunTestImportRCA} disabled={testImportingRCA || loadingFullImportConfig || !rcaWiqlQuery.trim()} startIcon={testImportingRCA ? <LinearProgress size={20} /> : null} sx={{ borderColor: 'grey.500', color: 'text.secondary', '&:hover': { borderColor: 'grey.700', backgroundColor: 'action.hover' } }} title="Fetch and sync first 100 records">{testImportingRCA ? 'Running...' : 'Run Test Import (100)'}</Button>
                      </Grid>
                      {rcaSyncResult && (
                        <Grid item xs={12}><Alert severity={rcaSyncResult.success ? 'success' : 'error'} onClose={() => setRcaSyncResult(null)}>{rcaSyncResult.message}{rcaSyncResult.success && <Typography variant="body2" sx={{ mt: 1 }}>Processed: {rcaSyncResult.processed || 0} | Created: {rcaSyncResult.created || 0} | Updated: {rcaSyncResult.updated || 0}</Typography>}</Alert></Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', background: 'white' }}>
                <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, '& .MuiAccordionSummary-content': { my: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <SettingsIcon sx={{ color: FULL_IMPORT_DARK_GREEN, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ color: FULL_IMPORT_DARK_GREEN, fontWeight: 'bold' }}>4. Technical Debts</Typography>
                      {lastTechnicalDebtSync && <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>Last sync: {format(new Date(lastTechnicalDebtSync), 'MMM d, yyyy HH:mm')}</Typography>}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, pt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline rows={6} label="Technical Debt WIQL Query" value={technicalDebtWiqlQuery} onChange={(e) => setTechnicalDebtWiqlQuery(e.target.value)}
                          placeholder="SELECT [System.Id], [System.Title] FROM WorkItems WHERE [System.WorkItemType] = 'Task' AND [System.Tags] CONTAINS 'TechnicalDebt'" disabled={loadingFullImportConfig || savingFullImportConfig} helperText="Enter WIQL query to fetch Technical Debt items from Azure DevOps. RCA and Technical Debt are kept in sync via Event Processing and Delta imports." />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Button variant="contained" size="small" onClick={handleSaveFullImportConfig} disabled={savingFullImportConfig || loadingFullImportConfig} startIcon={savingFullImportConfig ? <LinearProgress size={20} /> : <SaveIcon />} sx={{ backgroundColor: FULL_IMPORT_DARK_GREEN, '&:hover': { backgroundColor: '#0f2e1f' } }}>{savingFullImportConfig ? 'Saving...' : 'Save Query'}</Button>
                        <Button variant="outlined" size="small" onClick={handleFullImportTabSyncTechnicalDebt} disabled={syncingTechnicalDebt || loadingFullImportConfig || !technicalDebtWiqlQuery.trim()} startIcon={syncingTechnicalDebt ? <LinearProgress size={20} /> : <SyncIcon />} sx={{ borderColor: FULL_IMPORT_DARK_GREEN, color: FULL_IMPORT_DARK_GREEN, '&:hover': { borderColor: '#0f2e1f', backgroundColor: 'rgba(29, 68, 52, 0.04)' } }}>{syncingTechnicalDebt ? 'Syncing...' : 'Sync Technical Debt Items'}</Button>
                        <Button variant="outlined" size="small" onClick={handleRunTestImportTechnicalDebt} disabled={testImportingTechnicalDebt || loadingFullImportConfig || !technicalDebtWiqlQuery.trim()} startIcon={testImportingTechnicalDebt ? <LinearProgress size={20} /> : null} sx={{ borderColor: 'grey.500', color: 'text.secondary', '&:hover': { borderColor: 'grey.700', backgroundColor: 'action.hover' } }} title="Fetch and sync first 100 records">{testImportingTechnicalDebt ? 'Running...' : 'Run Test Import (100)'}</Button>
                      </Grid>
                      {technicalDebtSyncResult && (
                        <Grid item xs={12}><Alert severity={technicalDebtSyncResult.success ? 'success' : 'error'} onClose={() => setTechnicalDebtSyncResult(null)}>{technicalDebtSyncResult.message}{technicalDebtSyncResult.success && <Typography variant="body2" sx={{ mt: 1 }}>Processed: {technicalDebtSyncResult.processed || 0} | Created: {technicalDebtSyncResult.created || 0} | Updated: {technicalDebtSyncResult.updated || 0}</Typography>}</Alert></Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', background: 'white' }}>
                <Typography variant="subtitle1" sx={{ color: FULL_IMPORT_DARK_GREEN, fontWeight: 'bold', mb: 2 }}>Work Item Types</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: FULL_IMPORT_DARK_GREEN, display: 'block', mb: 1 }}>Work Items — common fields & by type</Typography>
                    <WorkItemsFieldsTables config={fullImportDisplayConfig?.workItems || FULL_IMPORT_FIELD_META.workItems} accentColor={FULL_IMPORT_DARK_GREEN} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: FULL_IMPORT_DARK_GREEN, display: 'block', mb: 1 }}>Test Case</Typography>
                    <FetchedCalculatedTable
                      fetched={(fullImportDisplayConfig?.testCase || FULL_IMPORT_FIELD_META.testCase).fetched}
                      calculated={(fullImportDisplayConfig?.testCase || FULL_IMPORT_FIELD_META.testCase).calculated}
                      accentColor={FULL_IMPORT_DARK_GREEN}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: FULL_IMPORT_DARK_GREEN, display: 'block', mb: 1 }}>RCA</Typography>
                    <FetchedCalculatedTable
                      fetched={(fullImportDisplayConfig?.rca || FULL_IMPORT_FIELD_META.rca).fetched}
                      calculated={(fullImportDisplayConfig?.rca || FULL_IMPORT_FIELD_META.rca).calculated}
                      accentColor={FULL_IMPORT_DARK_GREEN}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: FULL_IMPORT_DARK_GREEN, display: 'block', mb: 1 }}>Technical Debt</Typography>
                    <FetchedCalculatedTable
                      fetched={(fullImportDisplayConfig?.technicalDebt || FULL_IMPORT_FIELD_META.technicalDebt).fetched}
                      calculated={(fullImportDisplayConfig?.technicalDebt || FULL_IMPORT_FIELD_META.technicalDebt).calculated}
                      accentColor={FULL_IMPORT_DARK_GREEN}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {/* Delta Imports Tab - Schedule left, Work Item Types right; no query URL required */}
          {tabValue === 2 && (
            <Box sx={isEventProcessingEnabled ? { opacity: 0.85, pointerEvents: 'none', userSelect: 'none' } : {}}>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Schedule - left */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Schedule</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        One schedule for all delta imports. Imports work items and test cases changed in the last {deltaImportConfig.hoursBack || 3} hours. Query URL not required.
                      </Typography>
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel>Run Every (Hours)</InputLabel>
                        <Select
                          value={cronToHours(deltaImportConfig.cronSchedule || '0 */2 * * *')}
                          label="Run Every (Hours)"
                          onChange={(e) => setDeltaImportConfig(prev => ({ ...prev, cronSchedule: hoursToCron(e.target.value) }))}
                          disabled={isSavingDeltaConfig || isEventProcessingEnabled}
                        >
                          <MenuItem value={1}>Every 1 hour</MenuItem>
                          <MenuItem value={2}>Every 2 hours</MenuItem>
                          <MenuItem value={3}>Every 3 hours</MenuItem>
                          <MenuItem value={4}>Every 4 hours</MenuItem>
                          <MenuItem value={6}>Every 6 hours</MenuItem>
                          <MenuItem value={12}>Every 12 hours</MenuItem>
                          <MenuItem value={24}>Every 24 hours</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Hours Back"
                        type="number"
                        value={deltaImportConfig.hoursBack || 3}
                        onChange={(e) => setDeltaImportConfig(prev => ({ ...prev, hoursBack: parseInt(e.target.value) || 3 }))}
                        fullWidth size="small" sx={{ mb: 1 }}
                        inputProps={{ min: 1, max: 24 }}
                        disabled={isSavingDeltaConfig || isEventProcessingEnabled}
                      />
                      <Button variant="outlined" size="small" onClick={handleSaveScheduleAll} disabled={isSavingDeltaConfig || isEventProcessingEnabled} fullWidth>
                        {isSavingDeltaConfig ? 'Saving...' : 'Save Schedule'}
                      </Button>
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" display="block">Work Items – Last Run: {formatDate(deltaImportConfig.lastRun)} · Next Run: {formatDate(deltaImportConfig.nextRun)}</Typography>
                        <Typography variant="caption" display="block">Tests – Last Run: {formatDate(testDeltaImportConfig.lastRun)} · Next Run: {formatDate(testDeltaImportConfig.nextRun)}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Work Item Types - right */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'action.hover' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1">Work Item Types</Typography>
                      <Tooltip title="Select which work item types to include. Delta import gets changes over the last N hours for all selected types.">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                    <FormGroup>
                      <Grid container spacing={2}>
                        {workItemTypes.map((type) => (
                          <Grid item xs={12} sm={6} key={type.key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedWorkItemTypes[type.key]}
                                  onChange={() => handleWorkItemTypeChange(type.key)}
                                  disabled={isAnyImportRunning() || isEventProcessingEnabled}
                                />
                              }
                              label={type.label}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  </Paper>
                </Grid>
              </Grid>

              {/* Run Delta – separate section (not part of schedule) */}
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Run Delta</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Import work items and test cases changed or created in the last N hours. Uses the work item types selected on the right.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Hours (1–24)</InputLabel>
                      <Select
                        value={Math.min(24, Math.max(1, runDeltaHoursBack || 4))}
                        label="Hours (1–24)"
                        onChange={(e) => setRunDeltaHoursBack(Number(e.target.value))}
                        disabled={isDeltaImportRunning || isTestDeltaImportRunning || isEventProcessingEnabled}
                      >
                        {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
                          <MenuItem key={h} value={h}>{h} {h === 1 ? 'hour' : 'hours'}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<Update />}
                      onClick={handleRunDelta}
                      disabled={isDeltaImportRunning || isTestDeltaImportRunning || connectionStatus !== 'success' || isEventProcessingEnabled}
                    >
                      {(isDeltaImportRunning || isTestDeltaImportRunning) ? 'Starting...' : 'Run Delta'}
                    </Button>
                  </Box>
                  {(isDeltaImportRunning || isTestDeltaImportRunning) && <LinearProgress sx={{ mt: 2 }} />}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Backup & Restore Tab */}
          {tabValue === 3 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Backups are stored on the server at the configured persistent mount. Full database only (no individual collections).
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Create backup</Typography>
                      <TextField
                        label="Backup name"
                        value={backupName}
                        onChange={(e) => setBackupName(e.target.value)}
                        placeholder="e.g. pre-release-2025"
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        disabled={isBackupRunning}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateBackup}
                        disabled={isBackupRunning || !(backupName || '').trim()}
                      >
                        {isBackupRunning ? 'Creating...' : 'Create backup'}
                      </Button>
                      {isBackupRunning && <LinearProgress sx={{ mt: 2 }} />}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Restore from backup</Typography>
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Select backup</InputLabel>
                        <Select
                          value={selectedBackup}
                          label="Select backup"
                          onChange={(e) => setSelectedBackup(e.target.value)}
                          disabled={isRestoreRunning || backups.length === 0}
                        >
                          <MenuItem value="">
                            <em>{backups.length === 0 ? 'No backups available' : 'Choose a backup'}</em>
                          </MenuItem>
                          {backups.map((b) => (
                            <MenuItem key={b.name} value={b.name}>
                              {b.name} ({new Date(b.createdAt).toLocaleString()})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleRestoreBackup}
                        disabled={isRestoreRunning || !selectedBackup}
                      >
                        {isRestoreRunning ? 'Restoring...' : 'Restore'}
                      </Button>
                      {isRestoreRunning && <LinearProgress sx={{ mt: 2 }} />}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadBackups} disabled={isBackupRunning || isRestoreRunning}>
                  Refresh backup list
                </Button>
              </Box>
            </Box>
          )}

          {/* Non-ADO Imports Tab */}
          {tabValue === 4 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enable or disable scheduled backend imports. When disabled, the process does not start with the server (or does not run in the periodic checker). When enabled, the delta/periodic import runs on its schedule.
              </Typography>
              {loadingNonAdoConfig && !nonAdoConfig ? (
                <LinearProgress sx={{ mb: 2 }} />
              ) : nonAdoConfig ? (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Scheduled non-ADO imports
                    </Typography>
                    <List disablePadding>
                      <ListItem divider>
                        <ListItemText
                          primary="Repository Sync (GitHub & Sonar)"
                          secondary="Daily at 12 AM. Syncs GitHub repo data and SonarQube metrics for all repositories."
                        />
                        <Switch
                          checked={nonAdoConfig.repositorySyncEnabled}
                          onChange={(e) => handleNonAdoToggle('repositorySyncEnabled', e.target.checked)}
                          disabled={savingNonAdoConfig}
                          color="primary"
                        />
                      </ListItem>
                      <ListItem divider>
                        <ListItemText
                          primary="SNYK"
                          secondary="Checks every 30 minutes. Syncs security issues from SNYK Export API."
                        />
                        <Switch
                          checked={nonAdoConfig.snykEnabled}
                          onChange={(e) => handleNonAdoToggle('snykEnabled', e.target.checked)}
                          disabled={savingNonAdoConfig}
                          color="primary"
                        />
                      </ListItem>
                      <ListItem divider>
                        <ListItemText
                          primary="Datadog"
                          secondary="Checks every 30 minutes and during scheduled delta import. Syncs product and microservice deployment data."
                        />
                        <Switch
                          checked={nonAdoConfig.datadogEnabled}
                          onChange={(e) => handleNonAdoToggle('datadogEnabled', e.target.checked)}
                          disabled={savingNonAdoConfig}
                          color="primary"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Accessibility"
                          secondary="Checks every 30 minutes. Syncs accessibility issues from configured ADO query."
                        />
                        <Switch
                          checked={nonAdoConfig.accessibilityEnabled}
                          onChange={(e) => handleNonAdoToggle('accessibilityEnabled', e.target.checked)}
                          disabled={savingNonAdoConfig}
                          color="primary"
                        />
                      </ListItem>
                    </List>
                    {savingNonAdoConfig && <LinearProgress sx={{ mt: 1 }} />}
                  </CardContent>
                </Card>
              ) : (
                <Typography color="text.secondary">Could not load non-ADO imports configuration.</Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>
      
    </Box>
  );
};

export default UnifiedImportSettings;

